import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';

export default function AdminLogin() {
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (tab === 'login') {
        await login(email, password);
      } else {
        await register(email, password);
      }
      navigate('/admin');
    } catch (err) {
      const msg = err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password'
        ? 'Email ou password incorretos.'
        : err.code === 'auth/email-already-in-use'
        ? 'Este email já está registado.'
        : err.code === 'auth/weak-password'
        ? 'A password deve ter pelo menos 6 caracteres.'
        : 'Erro ao autenticar. Verifique os dados.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a
            href="/"
            className="inline-block font-bold text-2xl text-zinc-900 tracking-tight"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="admin-login-logo"
          >
            RJ <span className="text-[#C2410C]">Montagens</span>
          </a>
          <p className="text-sm text-zinc-500 mt-1">Painel de Administração</p>
        </div>

        <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-zinc-100">
            <button
              onClick={() => { setTab('login'); setError(''); }}
              className={`flex-1 py-4 text-sm font-semibold transition-colors ${tab === 'login' ? 'text-zinc-900 border-b-2 border-[#C2410C]' : 'text-zinc-400 hover:text-zinc-600'}`}
              data-testid="admin-tab-login"
            >
              Entrar
            </button>
            <button
              onClick={() => { setTab('register'); setError(''); }}
              className={`flex-1 py-4 text-sm font-semibold transition-colors ${tab === 'register' ? 'text-zinc-900 border-b-2 border-[#C2410C]' : 'text-zinc-400 hover:text-zinc-600'}`}
              data-testid="admin-tab-register"
            >
              Primeiro Acesso
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-5" data-testid="admin-login-form">
            {tab === 'register' && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700">
                Use esta opção apenas na configuração inicial. Após criar a conta, utilize o separador "Entrar".
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1.5">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="admin@email.com"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#C2410C]/30 focus:border-[#C2410C] text-sm text-zinc-900 placeholder:text-zinc-400 transition-all"
                  data-testid="admin-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1.5">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full pl-10 pr-11 py-3 rounded-xl border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#C2410C]/30 focus:border-[#C2410C] text-sm text-zinc-900 placeholder:text-zinc-400 transition-all"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm" data-testid="admin-login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-[#C2410C] hover:bg-[#9A3412] disabled:opacity-60 text-white font-semibold rounded-full transition-all duration-200 active:scale-95 text-sm"
              data-testid="admin-login-submit"
            >
              {loading ? 'A processar...' : tab === 'login' ? 'Entrar' : 'Criar conta'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-zinc-400 mt-4">
          <a href="/" className="hover:text-zinc-700 transition-colors">← Voltar ao site</a>
        </p>
      </div>
    </div>
  );
}
